// 振り返りレポート画面 (spec §4.2)。
// 現状: Timeline / Heatmap / Sections / HostActions / Participants の最低限。
// Cloud Recording / AI Companion トランスクリプトのインライン表示と過去研修比較は M11 で。

import {
  useEffect,
  useState,
  type ReactElement,
} from 'react';
import { t, type Locale, type MessageKey } from '@solo-webinar-copilot/i18n';
import type {
  ButtonEvent,
  ButtonType,
  HostAction,
  SectionMarker,
  Session,
} from '@solo-webinar-copilot/shared';
import {
  fetchComparisons,
  fetchHeatmap,
  fetchParticipants,
  fetchSections,
  fetchSession,
  fetchSuggestions,
  fetchTimeline,
  generateSuggestions,
  type ComparisonSummary,
  type HeatmapBucket,
  type ImprovementSuggestion,
  type ParticipantStats,
  type SectionStats,
} from '../lib/api.ts';

const BUTTON_TYPES: readonly ButtonType[] = [
  'slow_down',
  'dont_understand',
  'done',
  'look_at_me',
  'ok_understood',
  'emergency',
  'have_question',
  'need_break',
];
const BUTTON_LABEL_KEY: Record<ButtonType, MessageKey> = {
  slow_down: 'in_client.button.slow_down',
  dont_understand: 'in_client.button.dont_understand',
  done: 'in_client.button.done',
  look_at_me: 'in_client.button.look_at_me',
  ok_understood: 'in_client.button.ok_understood',
  emergency: 'in_client.button.emergency',
  have_question: 'in_client.button.have_question',
  need_break: 'in_client.button.need_break',
};

export interface ReportProps {
  sessionId: string;
  locale: Locale;
}

interface State {
  loading: boolean;
  error: string | null;
  session: Session | null;
  events: ButtonEvent[];
  hostActions: HostAction[];
  buckets: HeatmapBucket[];
  startedAt: number | null;
  sections: SectionStats[];
  participants: ParticipantStats[];
  suggestionsSummary: string | null;
  suggestions: ImprovementSuggestion[];
  suggestionsGeneratedAt: number | null;
  suggestionsLoading: boolean;
  suggestionsError: string | null;
  comparisons: ComparisonSummary[];
  comparisonsTemplateId: string | null;
}

const INITIAL: State = {
  loading: true,
  error: null,
  session: null,
  events: [],
  hostActions: [],
  buckets: [],
  startedAt: null,
  sections: [],
  participants: [],
  suggestionsSummary: null,
  suggestions: [],
  suggestionsGeneratedAt: null,
  suggestionsLoading: false,
  suggestionsError: null,
  comparisons: [],
  comparisonsTemplateId: null,
};

export function Report({ sessionId, locale }: ReportProps): ReactElement {
  const [state, setState] = useState<State>(INITIAL);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const [s, tl, hm, sec, pa, sug, cmp] = await Promise.all([
        fetchSession(sessionId),
        fetchTimeline(sessionId),
        fetchHeatmap(sessionId),
        fetchSections(sessionId),
        fetchParticipants(sessionId),
        fetchSuggestions(sessionId),
        fetchComparisons(sessionId),
      ]);
      if (cancelled) return;
      const firstFail = [s, tl, hm, sec, pa, sug, cmp].find((x) => !x.ok);
      if (firstFail && !firstFail.ok) {
        setState({ ...INITIAL, loading: false, error: firstFail.error });
        return;
      }
      const sugItem = sug.ok ? sug.data.item : null;
      const parsedSuggestions: ImprovementSuggestion[] =
        sugItem && sugItem.ai_improvement_suggestions
          ? safeParseSuggestions(sugItem.ai_improvement_suggestions)
          : [];
      setState({
        loading: false,
        error: null,
        session: s.ok ? s.data.session : null,
        events: tl.ok ? tl.data.events : [],
        hostActions: tl.ok ? tl.data.hostActions : [],
        buckets: hm.ok ? hm.data.buckets : [],
        startedAt: hm.ok ? hm.data.startedAt ?? null : null,
        sections: sec.ok ? sec.data.sections : [],
        participants: pa.ok ? pa.data.items : [],
        suggestionsSummary: sugItem?.ai_summary ?? null,
        suggestions: parsedSuggestions,
        suggestionsGeneratedAt: sugItem?.generated_at ?? null,
        suggestionsLoading: false,
        suggestionsError: null,
        comparisons: cmp.ok ? cmp.data.items : [],
        comparisonsTemplateId: cmp.ok ? cmp.data.templateId : null,
      });
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const handleGenerateSuggestions = async () => {
    setState((prev) => ({
      ...prev,
      suggestionsLoading: true,
      suggestionsError: null,
    }));
    const r = await generateSuggestions(sessionId);
    if (r.ok) {
      setState((prev) => ({
        ...prev,
        suggestionsLoading: false,
        suggestionsSummary: r.data.summary,
        suggestions: r.data.suggestions,
        suggestionsGeneratedAt: r.data.generatedAt,
      }));
    } else {
      setState((prev) => ({
        ...prev,
        suggestionsLoading: false,
        suggestionsError: r.error,
      }));
    }
  };

  if (state.loading) {
    return (
      <main data-testid="report-loading">
        <p>{t('web.report.loading', locale)}</p>
      </main>
    );
  }
  if (state.error) {
    return (
      <main data-testid="report-error">
        <p role="alert">
          {t('web.report.error', locale)}: {state.error}
        </p>
      </main>
    );
  }
  if (!state.session) return <main>—</main>;

  return (
    <main data-testid="report">
      <h1>{t('web.report.title', locale)}</h1>
      <section>
        <p data-testid="report-meeting">
          Meeting: <code>{state.session.zoom_meeting_id}</code>
        </p>
        <p>
          {t('web.report.status', locale)}: <code>{state.session.status}</code>
          {state.session.cloud_recording_url ? (
            <>
              {' · '}
              <a
                href={state.session.cloud_recording_url}
                target="_blank"
                rel="noreferrer"
                data-testid="report-recording-link"
              >
                {t('web.report.recording', locale)}
              </a>
            </>
          ) : null}
        </p>
      </section>

      <SuggestionsView
        locale={locale}
        summary={state.suggestionsSummary}
        suggestions={state.suggestions}
        generatedAt={state.suggestionsGeneratedAt}
        loading={state.suggestionsLoading}
        error={state.suggestionsError}
        onGenerate={() => void handleGenerateSuggestions()}
      />
      <ComparisonsView
        locale={locale}
        currentSession={state.session}
        comparisons={state.comparisons}
        templateId={state.comparisonsTemplateId}
      />
      <SectionsView locale={locale} sections={state.sections} />
      <HeatmapView locale={locale} buckets={state.buckets} />
      <ParticipantsView locale={locale} items={state.participants} />
      <TimelineView
        locale={locale}
        events={state.events}
        hostActions={state.hostActions}
        cloudRecordingUrl={state.session.cloud_recording_url}
        sessionStartedAt={state.session.started_at}
      />
    </main>
  );
}

function ComparisonsView({
  locale,
  currentSession,
  comparisons,
  templateId,
}: {
  locale: Locale;
  currentSession: Session;
  comparisons: ComparisonSummary[];
  templateId: string | null;
}): ReactElement {
  if (!templateId) {
    return (
      <section data-testid="report-comparisons">
        <h2>{t('web.report.comparisons.title', locale)}</h2>
        <p data-testid="comparisons-no-template">
          {t('web.report.comparisons.no_template', locale)}
        </p>
      </section>
    );
  }
  if (comparisons.length === 0) {
    return (
      <section data-testid="report-comparisons">
        <h2>{t('web.report.comparisons.title', locale)}</h2>
        <p data-testid="comparisons-empty">
          {t('web.report.comparisons.empty', locale)}
        </p>
      </section>
    );
  }

  // 現在セッションの集約値を計算 (button_events から再集計するか、参加者押下数で代替)。
  // ここは API の participants 集計を流用する形にしてもよいが、表示上は
  // "現在の進行中" は durationSec / participantCount のみで簡易表示する。
  const currentDuration =
    currentSession.started_at && currentSession.ended_at
      ? Math.floor(
          (currentSession.ended_at - currentSession.started_at) / 1000,
        )
      : null;

  return (
    <section data-testid="report-comparisons">
      <h2>{t('web.report.comparisons.title', locale)}</h2>
      <table>
        <thead>
          <tr>
            <th>{t('web.report.comparisons.session', locale)}</th>
            <th>{t('web.report.comparisons.duration', locale)}</th>
            <th>{t('in_client.button.dont_understand', locale)}</th>
            <th>{t('in_client.button.done', locale)}</th>
            <th>{t('in_client.button.have_question', locale)}</th>
          </tr>
        </thead>
        <tbody>
          <tr data-testid="comparison-current">
            <td>
              <strong>{t('web.report.comparisons.current', locale)}</strong>
            </td>
            <td>
              {currentDuration !== null ? formatDuration(currentDuration) : '—'}
            </td>
            <td>—</td>
            <td>—</td>
            <td>—</td>
          </tr>
          {comparisons.map((c) => (
            <tr key={c.sessionId} data-testid={`comparison-${c.sessionId}`}>
              <td>
                <a href={`/reports/${encodeURIComponent(c.sessionId)}`}>
                  {c.startedAt
                    ? new Date(c.startedAt).toISOString().slice(0, 10)
                    : c.sessionId.slice(0, 8)}
                </a>
              </td>
              <td>
                {c.durationSeconds !== null
                  ? formatDuration(c.durationSeconds)
                  : '—'}
              </td>
              <td>{c.totalsByButton['dont_understand'] ?? 0}</td>
              <td>{c.totalsByButton['done'] ?? 0}</td>
              <td>{c.totalsByButton['have_question'] ?? 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function safeParseSuggestions(json: string): ImprovementSuggestion[] {
  try {
    const parsed = JSON.parse(json) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (x): x is ImprovementSuggestion =>
        typeof x === 'object' &&
        x !== null &&
        typeof (x as { category?: unknown }).category === 'string' &&
        typeof (x as { message?: unknown }).message === 'string',
    );
  } catch {
    return [];
  }
}

function SuggestionsView({
  locale,
  summary,
  suggestions,
  generatedAt,
  loading,
  error,
  onGenerate,
}: {
  locale: Locale;
  summary: string | null;
  suggestions: ImprovementSuggestion[];
  generatedAt: number | null;
  loading: boolean;
  error: string | null;
  onGenerate: () => void;
}): ReactElement {
  return (
    <section data-testid="report-suggestions">
      <h2>
        {t('web.report.suggestions.title', locale)}{' '}
        <small style={{ opacity: 0.7 }}>
          [{t('in_client.ai.generated_label', locale)}]
        </small>
      </h2>
      <button
        type="button"
        onClick={onGenerate}
        disabled={loading}
        data-testid="suggestions-generate-button"
      >
        {loading
          ? t('web.report.suggestions.generating', locale)
          : generatedAt
            ? t('web.report.suggestions.regenerate', locale)
            : t('web.report.suggestions.generate', locale)}
      </button>
      {error ? (
        <p role="alert" data-testid="suggestions-error">
          {error}
        </p>
      ) : null}
      {summary ? (
        <p data-testid="suggestions-summary" style={{ marginTop: 8 }}>
          {summary}
        </p>
      ) : null}
      {suggestions.length > 0 ? (
        <ol data-testid="suggestions-list">
          {suggestions.map((s, i) => (
            <li key={i} data-testid={`suggestion-${i}`}>
              <strong>[{s.category}]</strong> {s.message}
              {s.evidence ? (
                <small style={{ opacity: 0.7, marginLeft: 6 }}>
                  ({s.evidence})
                </small>
              ) : null}
            </li>
          ))}
        </ol>
      ) : null}
      <p style={{ fontSize: 12, opacity: 0.6 }}>
        {t('in_client.ai.disclaimer', locale)}
      </p>
    </section>
  );
}

function SectionsView({
  locale,
  sections,
}: {
  locale: Locale;
  sections: SectionStats[];
}): ReactElement {
  return (
    <section data-testid="report-sections">
      <h2>{t('web.report.sections', locale)}</h2>
      {sections.length === 0 ? (
        <p>—</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>{t('web.report.section_name', locale)}</th>
              <th>{t('web.report.section_type', locale)}</th>
              <th>{t('web.report.duration', locale)}</th>
              <th title={t('in_client.button.done', locale)}>
                {t('web.report.section_done', locale)}
              </th>
              <th title={t('in_client.button.dont_understand', locale)}>
                {t('in_client.button.dont_understand', locale)}
              </th>
              <th title={t('in_client.button.slow_down', locale)}>
                {t('in_client.button.slow_down', locale)}
              </th>
              <th title={t('in_client.button.have_question', locale)}>
                {t('in_client.button.have_question', locale)}
              </th>
              <th title={t('in_client.button.emergency', locale)}>
                {t('in_client.button.emergency', locale)}
              </th>
            </tr>
          </thead>
          <tbody>
            {sections.map((s) => (
              <tr key={s.marker.id} data-testid={`section-row-${s.marker.id}`}>
                <td>{s.marker.name}</td>
                <td>{s.marker.type}</td>
                <td>
                  {s.durationSeconds !== null
                    ? formatDuration(s.durationSeconds)
                    : t('web.report.section_active', locale)}
                </td>
                <td>{s.doneCount}</td>
                <td data-testid={`section-cell-${s.marker.id}-dont_understand`}>
                  {s.pressedByType.dont_understand ?? 0}
                </td>
                <td>{s.pressedByType.slow_down ?? 0}</td>
                <td>{s.pressedByType.have_question ?? 0}</td>
                <td>{s.pressedByType.emergency ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function HeatmapView({
  locale,
  buckets,
}: {
  locale: Locale;
  buckets: HeatmapBucket[];
}): ReactElement {
  // minute × button_type のテーブル。
  const minutes = Array.from(new Set(buckets.map((b) => b.minute))).sort(
    (a, b) => a - b,
  );
  const matrix = new Map<string, number>();
  for (const b of buckets) matrix.set(`${b.minute}:${b.buttonType}`, b.count);

  return (
    <section data-testid="report-heatmap">
      <h2>{t('web.report.heatmap', locale)}</h2>
      {minutes.length === 0 ? (
        <p>—</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>{t('web.report.minute', locale)}</th>
              {BUTTON_TYPES.map((bt) => (
                <th key={bt}>{t(BUTTON_LABEL_KEY[bt], locale)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {minutes.map((m) => (
              <tr key={m} data-testid={`heatmap-row-${m}`}>
                <td>{m}</td>
                {BUTTON_TYPES.map((bt) => (
                  <td key={bt}>{matrix.get(`${m}:${bt}`) ?? 0}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function ParticipantsView({
  locale,
  items,
}: {
  locale: Locale;
  items: ParticipantStats[];
}): ReactElement {
  return (
    <section data-testid="report-participants">
      <h2>{t('web.report.participants', locale)}</h2>
      {items.length === 0 ? (
        <p>—</p>
      ) : (
        <ul>
          {items.map((p) => (
            <li key={p.participantId} data-testid={`participant-row-${p.participantId}`}>
              {p.participantName ?? p.participantId}: {p.pressCount}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function TimelineView({
  locale,
  events,
  hostActions,
  cloudRecordingUrl,
  sessionStartedAt,
}: {
  locale: Locale;
  events: ButtonEvent[];
  hostActions: HostAction[];
  cloudRecordingUrl: string | null;
  sessionStartedAt: number | null;
}): ReactElement {
  // ボタンイベントとホストアクションを 1 本のタイムラインに混ぜる。
  type Row =
    | { kind: 'event'; ev: ButtonEvent }
    | { kind: 'action'; ha: HostAction };
  const rows: Row[] = [
    ...events.map<Row>((ev) => ({ kind: 'event', ev })),
    ...hostActions.map<Row>((ha) => ({ kind: 'action', ha })),
  ].sort((a, b) => {
    const ta = a.kind === 'event' ? a.ev.occurred_at : a.ha.occurred_at;
    const tb = b.kind === 'event' ? b.ev.occurred_at : b.ha.occurred_at;
    return ta - tb;
  });

  // Cloud Recording の該当時刻にジャンプする URL を組み立てる (spec §6.6)。
  // Zoom の query param 仕様は要検証 (spec §13.4)。MVP では `?startTime=<ms>` を採用、
  // 既存の query があれば & で連結。
  const recordingJumpUrl = (occurredAt: number): string | null => {
    if (!cloudRecordingUrl || !sessionStartedAt) return null;
    const offsetMs = Math.max(0, occurredAt - sessionStartedAt);
    const sep = cloudRecordingUrl.includes('?') ? '&' : '?';
    return `${cloudRecordingUrl}${sep}startTime=${offsetMs}`;
  };

  return (
    <section data-testid="report-timeline">
      <h2>{t('web.report.timeline', locale)}</h2>
      {rows.length === 0 ? (
        <p>—</p>
      ) : (
        <ol>
          {rows.map((r, i) => {
            const ts = r.kind === 'event' ? r.ev.occurred_at : r.ha.occurred_at;
            const jump = recordingJumpUrl(ts);
            const jumpLink = jump ? (
              <>
                {' '}
                <a
                  href={jump}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`timeline-jump-${i}`}
                  title={t('web.report.recording_jump_title', locale)}
                  style={{ marginLeft: 4 }}
                >
                  ▶
                </a>
              </>
            ) : null;
            return r.kind === 'event' ? (
              <li key={`e-${r.ev.id}`} data-testid={`timeline-${i}`}>
                <code>{formatTime(r.ev.occurred_at)}</code>
                {jumpLink} ·{' '}
                <strong>{r.ev.participant_name ?? r.ev.participant_id}</strong>:{' '}
                {t(BUTTON_LABEL_KEY[r.ev.button_type], locale)} ({r.ev.state})
              </li>
            ) : (
              <li key={`a-${r.ha.id}`} data-testid={`timeline-${i}`}>
                <code>{formatTime(r.ha.occurred_at)}</code>
                {jumpLink} · <em>{r.ha.action_type}</em>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

function formatTime(ts: number): string {
  return new Date(ts).toISOString().slice(11, 19);
}

function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
